import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui';
import { useApiClient } from '../hooks';
import { useAppDispatch } from '../store';
import { setAuth } from '../store/slices/authSlice';
import apiClient from '../utils/api/ApiClient';
import { API_ENDPOINTS } from '../constants/api.constants';
import type {
    VerifyOTPRequest,
    VerifyOTPResponse,
    SendOTPRequest,
    SendOTPResponse,
} from '../types/api.types';

/**
 * OTP Verification page
 */
export default function OTPVerification() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const phoneNumber = location.state?.phoneNumber || '';

    const { loading: isLoading, error, callApi, setError } = useApiClient();
    const { callApi: callResendApi } = useApiClient();

    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [resendTimer, setResendTimer] = useState(30);
    const [canResend, setCanResend] = useState(false);

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Redirect if no phone number
    useEffect(() => {
        if (!phoneNumber) {
            navigate('/login');
        }
    }, [phoneNumber, navigate]);

    // Countdown timer for resend
    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [resendTimer]);

    const handleOtpChange = (index: number, value: string) => {
        // Only allow numbers
        if (value && !/^\d+$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.slice(-1); // Only take last character
        setOtp(newOtp);
        setError('');

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }

        // Auto-submit when all fields are filled
        if (newOtp.every((digit) => digit !== '') && index === 5) {
            handleVerify(newOtp.join(''));
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6).split('');
        if (pastedData.every((char) => /^\d+$/.test(char))) {
            const newOtp = [...otp];
            pastedData.forEach((char, idx) => {
                if (idx < 6) newOtp[idx] = char;
            });
            setOtp(newOtp);
            if (pastedData.length === 6) {
                handleVerify(newOtp.join(''));
            }
        }
    };

    const handleVerify = async (otpValue?: string) => {
        const otpToVerify = otpValue || otp.join('');

        if (otpToVerify.length !== 6) {
            setError('Please enter complete OTP');
            return;
        }

        setError(null);

        // Call verify OTP API
        const requestData: VerifyOTPRequest = {
            phone: phoneNumber,
            otp: otpToVerify,
        };

        await callApi<VerifyOTPResponse>(
            () => apiClient.post(API_ENDPOINTS.AUTH.VERIFY_OTP, requestData),
            (response) => {
                // Success - store auth data in Redux and localStorage
                const { token, adminUser } = response.data;

                // Set token in apiClient
                apiClient.setAuthToken(token);

                // Dispatch to Redux
                dispatch(setAuth({ user: adminUser, token }));

                // Navigate to dashboard
                navigate('/dashboard');
            },
            (errorMessage) => {
                // Error is already set by useApiClient
                console.error('Verify OTP error:', errorMessage);
                // Clear OTP on error
                setOtp(['', '', '', '', '', '']);
                inputRefs.current[0]?.focus();
            }
        );
    };

    const handleResend = async () => {
        if (!canResend) return;

        // Reset UI state
        setCanResend(false);
        setResendTimer(30);
        setOtp(['', '', '', '', '', '']);
        setError(null);
        inputRefs.current[0]?.focus();

        // Call resend OTP API (same as send OTP)
        const requestData: SendOTPRequest = {
            phone: phoneNumber,
        };

        await callResendApi<SendOTPResponse>(
            () => apiClient.post(API_ENDPOINTS.AUTH.SEND_OTP, requestData),
            () => {
                console.log('OTP resent successfully');
            },
            (errorMessage) => {
                console.error('Resend OTP error:', errorMessage);
            }
        );
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] via-[#f1f5f9] to-[#e2e8f0] relative overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-72 h-72 bg-[#6e64f0] opacity-10 rounded-full blur-3xl animate-pulse"></div>
                <div
                    className="absolute bottom-20 right-10 w-96 h-96 bg-[#10b981] opacity-10 rounded-full blur-3xl animate-pulse"
                    style={{ animationDelay: '1s' }}
                ></div>
            </div>

            {/* OTP Card */}
            <div className="relative z-10 w-full max-w-md px-6">
                <div className="bg-white rounded-2xl shadow-2xl p-8 border border-[#e2e8f0]">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate('/login')}
                        className="flex items-center gap-2 text-[#64748b] hover:text-[#1e293b] transition-colors mb-6"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 19l-7-7m0 0l7-7m-7 7h18"
                            />
                        </svg>
                        Back
                    </button>

                    {/* Icon */}
                    <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#6e64f0]/10 to-[#8b7cf6]/10 flex items-center justify-center">
                            <svg
                                className="w-8 h-8 text-[#6e64f0]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                />
                            </svg>
                        </div>
                    </div>

                    {/* Title */}
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-[#1e293b] mb-2">Verify OTP</h1>
                        <p className="text-[#64748b]">
                            We've sent a 6-digit code to{' '}
                            <span className="font-medium text-[#1e293b]">+91 {phoneNumber}</span>
                        </p>
                    </div>

                    {/* OTP Input */}
                    <div className="mb-6">
                        <div className="flex justify-center gap-3 mb-4">
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => {
                                        inputRefs.current[index] = el;
                                    }}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onPaste={index === 0 ? handlePaste : undefined}
                                    className={`w-12 h-14 text-center text-2xl font-semibold border-2 rounded-lg transition-all duration-200 ${
                                        digit
                                            ? 'border-[#6e64f0] bg-[#6e64f0]/5'
                                            : 'border-[#e2e8f0]'
                                    } focus:border-[#6e64f0] focus:outline-none focus:ring-2 focus:ring-[#6e64f0]/20 text-[#1e293b]`}
                                    autoFocus={index === 0}
                                />
                            ))}
                        </div>

                        {error && (
                            <p className="text-sm text-[#ef4444] text-center flex items-center justify-center gap-1">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                {error}
                            </p>
                        )}
                    </div>

                    {/* Verify Button */}
                    <Button
                        onClick={() => handleVerify()}
                        variant="primary"
                        size="large"
                        isLoading={isLoading}
                        disabled={otp.some((digit) => !digit)}
                        className="w-full mb-6"
                    >
                        {isLoading ? 'Verifying...' : 'Verify OTP'}
                    </Button>

                    {/* Resend */}
                    <div className="text-center">
                        <p className="text-sm text-[#64748b] mb-2">Didn't receive the code?</p>
                        {canResend ? (
                            <button
                                onClick={handleResend}
                                className="text-sm font-medium text-[#6e64f0] hover:text-[#5b50e8] transition-colors"
                            >
                                Resend OTP
                            </button>
                        ) : (
                            <p className="text-sm text-[#64748b]">
                                Resend in{' '}
                                <span className="font-medium text-[#1e293b]">{resendTimer}s</span>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
